import type { JSONContent } from "@tiptap/core";

type ImageDimensions = { width: number; height: number };
type ImageExportOptions = {
  inlineImageSource?: (src: string) => Promise<string | null>;
  maxHeight: number;
  maxWidth: number;
  resolveImageDimensions: (src: string) => Promise<ImageDimensions | null>;
};
type ImageExportTarget = "docx" | "pdf";

const EXPORT_IMAGE_BOUNDS = {
  docx: { maxHeight: 931, maxWidth: 602 },
  pdf: { maxHeight: 896, maxWidth: 566 },
};

const positiveNumber = (value: unknown) => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && /^\d+(\.\d+)?(px)?$/i.test(value.trim())
        ? Number.parseFloat(value)
        : NaN;

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const withoutHeight = (attrs: Record<string, unknown>) => {
  const { height: _height, ...nextAttrs } = attrs;
  return nextAttrs;
};

export const resolveBrowserImageDimensions = (
  src: string
): Promise<ImageDimensions | null> =>
  new Promise((resolve) => {
    if (typeof Image === "undefined") {
      resolve(null);
      return;
    }

    const image = new Image();
    let settled = false;
    const settle = (dimensions: ImageDimensions | null) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      image.onload = null;
      image.onerror = null;
      resolve(dimensions);
    };
    const dimensions = () => {
      const width = positiveNumber(image.naturalWidth);
      const height = positiveNumber(image.naturalHeight);
      return width && height ? { width, height } : null;
    };
    const timeoutId = setTimeout(() => settle(null), 2000);

    image.onload = () => settle(dimensions());
    image.onerror = () => settle(null);
    image.src = src;

    if (image.complete) {
      settle(dimensions());
    }
  });

const readBlobAsDataUrl = (blob: Blob): Promise<string | null> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });

const convertImageBlobToPngDataUrl = (blob: Blob): Promise<string | null> =>
  new Promise((resolve) => {
    if (
      typeof document === "undefined" ||
      typeof Image === "undefined" ||
      typeof URL === "undefined" ||
      typeof URL.createObjectURL !== "function" ||
      typeof URL.revokeObjectURL !== "function"
    ) {
      resolve(null);
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    const settle = (value: string | null) => {
      image.onload = null;
      image.onerror = null;
      URL.revokeObjectURL(objectUrl);
      resolve(value);
    };

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext("2d");

      if (!canvas.width || !canvas.height || !context) {
        settle(null);
        return;
      }

      context.drawImage(image, 0, 0);
      settle(canvas.toDataURL("image/png"));
    };
    image.onerror = () => settle(null);
    image.src = objectUrl;
  });

export const resolveBrowserImageDataUrl = async (
  src: string
): Promise<string | null> => {
  if (
    src.startsWith("data:") ||
    typeof fetch === "undefined" ||
    typeof FileReader === "undefined"
  ) {
    return null;
  }

  try {
    const response = await fetch(src);
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    const mimeType = blob.type.toLowerCase();
    const pngDataUrl =
      mimeType.startsWith("image/") &&
      mimeType !== "image/gif" &&
      mimeType !== "image/jpeg" &&
      mimeType !== "image/png"
        ? await convertImageBlobToPngDataUrl(blob)
        : null;

    return pngDataUrl ?? (await readBlobAsDataUrl(blob));
  } catch {
    return null;
  }
};

const constrainImageAttrs = async (
  attrs: Record<string, unknown>,
  {
    inlineImageSource,
    maxHeight,
    maxWidth,
    resolveImageDimensions,
  }: ImageExportOptions
) => {
  const explicitWidth = positiveNumber(attrs.width);
  const explicitHeight = positiveNumber(attrs.height);
  const dimensions =
    typeof attrs.src === "string" &&
    (explicitWidth === null || explicitHeight === null)
      ? await resolveImageDimensions(attrs.src).catch(() => null)
      : null;
  const width = explicitWidth ?? dimensions?.width ?? null;
  const height =
    explicitHeight ??
    (dimensions && width
      ? (width * dimensions.height) / dimensions.width
      : null);
  const maxExportWidth =
    width && height && height > maxHeight
      ? Math.min(
          maxWidth,
          Math.max(1, Math.floor((width * maxHeight) / height))
        )
      : maxWidth;
  const cappedAttrs =
    width !== null && width > maxExportWidth
      ? { ...withoutHeight(attrs), width: maxExportWidth }
      : attrs;

  const src = typeof cappedAttrs.src === "string" ? cappedAttrs.src : null;
  const dataUrl =
    src && inlineImageSource
      ? await inlineImageSource(src).catch(() => null)
      : null;

  return dataUrl ? { ...cappedAttrs, src: dataUrl } : cappedAttrs;
};

export const constrainTiptapExportImages = async (
  document: JSONContent,
  {
    inlineImageSource,
    maxHeight = EXPORT_IMAGE_BOUNDS.docx.maxHeight,
    maxWidth = EXPORT_IMAGE_BOUNDS.docx.maxWidth,
    resolveImageDimensions = resolveBrowserImageDimensions,
  }: Partial<ImageExportOptions> = {}
): Promise<JSONContent> => {
  const options = {
    inlineImageSource,
    maxHeight,
    maxWidth,
    resolveImageDimensions,
  };
  const content = Array.isArray(document.content)
    ? await Promise.all(
        document.content.map((child) =>
          constrainTiptapExportImages(child, options)
        )
      )
    : document.content;

  if (document.type === "paragraph" && (!content || content.length === 0)) {
    return { ...document, content: [{ type: "text", text: "\u00A0" }] };
  }

  if (document.type !== "image") {
    return content === document.content
      ? { ...document }
      : { ...document, content };
  }

  const attrs = document.attrs
    ? await constrainImageAttrs(document.attrs, options)
    : document.attrs;

  return {
    ...document,
    ...(content === document.content ? {} : { content }),
    ...(attrs === document.attrs ? {} : { attrs }),
  };
};

export const prepareTiptapExportImages = (
  document: JSONContent,
  target: ImageExportTarget
) =>
  constrainTiptapExportImages(document, {
    inlineImageSource: resolveBrowserImageDataUrl,
    ...EXPORT_IMAGE_BOUNDS[target],
  });
